import { z } from "zod";
import { makeMapNetworkErrorToCartError } from "~/core/cart-v2/blueprints/operations/map-network-error-to-cart-error.js";
import { makeMapParseErrorToCartError } from "~/core/cart-v2/blueprints/operations/map-parse-error-to-cart-error.js";
import { AddItemsPayload } from "~/core/cart-v2/operations/add-items/payload.ts";
import { postJson } from "~/core/network/post-json.js";
import { atShopifyRoot } from "~/core/network/shopify-root.js";

export type {
    AddItemsPayload,
    SingleItemPayload,
} from "~/core/cart-v2/operations/add-items/payload.ts";

// Response schemas
export const OptionsWithValues = z.object({
    name: z.string(),
    value: z.string(),
});

export type OptionsWithValues = z.infer<typeof OptionsWithValues>;

export const ItemAddedToCart = z.object({
    key: z.string(),
    quantity: z.number().min(1).optional().default(1),
});
export type ItemAddedToCart = z.infer<typeof ItemAddedToCart>;

export const ItemsAddedToCart = z.object({
    items: z.array(ItemAddedToCart),
});
export type ItemsAddedToCart = z.infer<typeof ItemsAddedToCart>;

export const addItems = async ({
    payload: _payload,
    signal,
}: {
    payload: AddItemsPayload;
    signal: AbortSignal;
}): Promise<ItemsAddedToCart> => {
    // Parse payload
    const payload = await AddItemsPayload.parseAsync(_payload).catch(
        makeMapParseErrorToCartError("Invalid add items payload"),
    );

    const _response = await postJson({
        url: atShopifyRoot("/cart/add.js"),
        json: payload,
        options: { signal },
    }).catch(
        makeMapNetworkErrorToCartError({
            userErrorMessage: "Failed to add items to cart",
            communicationErrorMessage:
                "Network error while adding items to cart",
        }),
    );

    const response = await ItemsAddedToCart.parseAsync(_response).catch(
        makeMapParseErrorToCartError("Invalid add items response"),
    );

    return response;
};
